import { A365BackendModel as Model } from '../a365.backendmodel';
import { Metamodel } from '../base/model';
import { AdapterRegistry } from './adapter-registry';
import _ from 'lodash';
import { NamedAdapter } from './named.adapter';
import { ModelConstants } from '../model-constants';
import { Database } from '../database/database';
import { IBaseModel, IDatabaseContent, IDDLItem } from '../database/interface';
import { ModelMarker } from '../services/state';
import { ModelUtilties } from '../services/model-utilities';
import { convertKeyToLowerCamelCase, convertKeyToUpperCamelCase } from '../../shared/utils/string.util';
import { DatabaseEntity, IExecuteChangeActionType } from '../database/databaseEntity';
import { TreeNamedResAndNameUtils } from '../../shared/utils/tree-named-naming.util';
import { isSyMsCDMDb } from '../../shared/utils/database.util';
import { Table } from '../table/table';
import { GlobalVariables } from '../../constants/globals';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(Database),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: '*'
    }
})
/* eslint-disable @typescript-eslint/no-explicit-any */
export class DatabaseAdapter extends NamedAdapter<Database, Model.DatabaseResource> {
    /**
     * If publish or forArmTemplate is true, return payload used in executeChangeWithValidation; otherwise, return the resource
     */
    protected async _toResource(database: Database, isPublish: boolean = false, forArmTemplate: boolean = false): Promise<{ [key: string]: any }> {
        if (!isPublish && !forArmTemplate) {
            const sourceContent = this._generatePureNewDatabaseEntity(database);
            return {
                name: database.name,
                properties: sourceContent,
                type: 'DATABASE'
            };
        }
        // Generate database payload for executeChangeWithValidation
        const databasePayload = this._generatePayload(database, forArmTemplate ? 'CREATE' : undefined);
        // Concat two kinds of payload
        const partPayload = databasePayload ? [databasePayload] : [];
        const entityPayloads = await this._fetchEntityChanges(database, isPublish, forArmTemplate);
        const payloads = partPayload.concat(_.flatten(entityPayloads));

        return forArmTemplate ? {
            name: database.name,
            properties: {
                Ddls: payloads
            },
            type: 'DATABASE'
        } : {
            payload: {
                Ddls: payloads
            },
            callback: (ddlItems: IDDLItem[]): void => {
                this._updateMDEntity(database, ddlItems); // update the object id, version and name which is needed in publishing
            },
            name: database.name,
            telemetryPayload: this._generateDatabasePayloadForTelemetry(database)
        };
    }

    protected _toModel(databaseResource: Model.DatabaseResource, database?: Database): Database {
        if (!database) {
            database = Database.MODEL.create<Database>();
        }

        // Set name, description and folder info to the named if there is any stored in resource.properties
        super._toModel(databaseResource, database);
        database.resourceType = ModelConstants.database + 's';

        const rawDatabaseContent = databaseResource.properties;
        if (rawDatabaseContent) {
            // Notice: the raw content is in UpperCamelCase
            const databaseContent = convertKeyToLowerCamelCase(databaseResource.properties);
            database.isSyMSCDMDatabase = isSyMsCDMDb(databaseResource);
            database.name = databaseResource.name;
            database.content = databaseContent;
            database.rawContentInJson = JSON.stringify(rawDatabaseContent);
            database.baseModel = this._getBaseModel(databaseContent);
            database.mDEntity = {
                name: databaseResource.name,
                objectVersion: databaseContent.objectVersion,
                objectId: databaseContent.objectId
            };

            if (databaseContent?.origin?.instanceId) {
                database.originInstanceId = databaseContent.origin.instanceId;
            }
        }

        return database;
    }

    private _getBaseModel(item: IDatabaseContent): IBaseModel {
        const properties = item.properties;
        const derivedModelDBInfoInString = properties && properties.derivedModelDBInfo ? properties.derivedModelDBInfo : undefined;
        const derivedModelDBInfo = convertKeyToLowerCamelCase(derivedModelDBInfoInString ? JSON.parse(derivedModelDBInfoInString) : undefined);
        const rawBaseModel = derivedModelDBInfo && derivedModelDBInfo.modelDirectives ? derivedModelDBInfo.modelDirectives.baseModel : undefined;

        return {
            ...rawBaseModel
        };

    }

    private _generatePayload(database: Database, actionType?: IExecuteChangeActionType): { [key: string]: any } {
        actionType = 'CREATE';

        if (actionType) {
            const newDatabaseEntity = this._generatePureNewDatabaseEntity(database);
            const oldDatabaseEntity = actionType !== 'CREATE' ? this._generatePureOldDatabaseEntity(database) : null;
            const entity = {
                ActionType: actionType,
                OldEntity: oldDatabaseEntity,
                NewEntity: newDatabaseEntity,
                Source: {
                    Type: 'SPARK'
                }
            };

            return entity;
        }
        return null;
    }

    private _generatePureNewDatabaseEntity(database: Database): { [key: string]: any } {
        const originalContent = convertKeyToUpperCamelCase(database.rawContentInJson ? JSON.parse(database.rawContentInJson) : {});
        const databaseContent = database.content;
        const baseModel = database.baseModel;
        const derivedModelDBInfo = baseModel && baseModel.name ? {
            ModelDirectives: {
                BaseModel: {
                    Name: baseModel.name,
                    Version: baseModel.version
                }
            }
        } : undefined;

        const properties = derivedModelDBInfo ? {
            DerivedModelDBInfo: JSON.stringify(derivedModelDBInfo)
        } : {};

        // We have to make the propety all lower case, so that it is not duplicated
        if (originalContent.Properties) {
            Object.keys(originalContent.Properties).forEach((key) => {
                if (key.match(/^x-ms-usageinfo$/i)) {
                    const newKey: string = key.replace(/^\S/g, (x) => x.toLowerCase());
                    originalContent.Properties[newKey] = originalContent.Properties[key];
                    delete originalContent.Properties[key];
                }
            });
        }

        // handle source
        const source = databaseContent.source ? {
            Provider: databaseContent.source.provider,
            Location: databaseContent.source.location,
            Properties: {
                FormatType: databaseContent.source.properties ? databaseContent.source.properties.formatType : 'csv',
                LinkedServiceName: databaseContent.source.properties ? databaseContent.source.properties.linkedServiceName : undefined
            }
        } : undefined;

        const entityProperties = {
            ...originalContent,
            Description: database.description ? database.description : databaseContent.description,
            Name: database.name,
            EntityType: 'DATABASE',
            Origin: {
                Type: 'SPARK'
            },
            Properties: {
                ...originalContent.Properties,
                ...properties,
                IsSyMSCDMDatabase: true
            },
            Source: {
                ...originalContent.Source,
                ...source
            }
        };

        if (database.originInstanceId) {
            (entityProperties.Origin as any).InstanceId = database.originInstanceId;
        }

        const coreProperties = {
            Name: database.name,
            EntityType: 'DATABASE',
        };

        const entity = GlobalVariables.useLakeDB2021Api ? {
            name: coreProperties.Name,
            type: coreProperties.EntityType,
            properties: { ...entityProperties }
        } : {
            ...coreProperties,
            ...entityProperties
        };

        return entity;
    }

    /**
     * Generate database entity which is used as oldEntity in executeChange
     * The entity would only contain necessary attribute instead of all
     */
    private _generatePureOldDatabaseEntity(database: Database): { [key: string]: any } {
        const originalName = database.mDEntity ? database.mDEntity.name : undefined;
        const entityProperties = {
            Origin: {
                Type: 'SPARK'
            },
            ObjectId: database.mDEntity ? database.mDEntity.objectId : undefined,
            ObjectVersion: database.mDEntity ? database.mDEntity.objectVersion : undefined
        };

        const coreProperties = {
            Name: originalName ? originalName : database.name,
            EntityType: 'DATABASE',
        };

        const entity = GlobalVariables.useLakeDB2021Api ? {
            name: coreProperties.Name,
            type: coreProperties.EntityType,
            properties: { ...entityProperties }
        } : {
            ...coreProperties,
            ...entityProperties
        };

        return entity;
    }

    private async _fetchEntityChanges(database: Database, isPublish: boolean, forArmTemplate: boolean): Promise<any[]> {
        // Get entities in a database
        const selectedNameds = database.entities || [];
        const payloadPromiseList: Array<Promise<{}>> = [];
        selectedNameds.forEach((selectedNamed: DatabaseEntity) => {
            // When generating arm template, status of named doesn't matter
            if (forArmTemplate || ModelMarker.isNew(selectedNamed) || ModelMarker.isDirty(selectedNamed) || ModelMarker.isDeleted(selectedNamed) || selectedNamed.alterAction) {
                const promise = ModelUtilties.toResource(this.context, selectedNamed, isPublish, forArmTemplate);
                payloadPromiseList.push(promise);
            }
        });

        const payloads = await Promise.all(payloadPromiseList);

        return payloads.map((payload: any) => payload.payload);
    }

    // update md entity
    private async _updateMDEntity(database: Database, ddlItems: IDDLItem[]): Promise<void> {
        if (!ddlItems) {
            return;
        }

        const databaseDDLItem = ddlItems.find((item) => item.EntityType === 'DATABASE');
        if (!databaseDDLItem) {
            return;
        }

        ddlItems.forEach((item: IDDLItem) => {
            if (item.EntityType === 'TABLE' || item.EntityType === 'RELATIONSHIP') {
                this._updateMDEntityForEntities(databaseDDLItem.EntityName, item, database.entities ? database.entities : []);
            } else if (item.EntityType === 'DATABASE') {
                this._updateMDEntityForDatabase(database, item);
            }
        });
    }

    private async _updateMDEntityForDatabase(database: Database, item: IDDLItem): Promise<void> {
        if (database) {
            database.mDEntity = {
                objectId: item.ObjectId,
                objectVersion: item.ObjectVersion,
                name: item.EntityName
            };
            // reset the alter action info in case the false using
            database.alterAction = undefined;
        }
    }

    private async _updateMDEntityForEntities(parentName: string, item: IDDLItem, entityNameds: DatabaseEntity[]): Promise<void> {
        const fullName = TreeNamedResAndNameUtils.correctName(parentName, item.EntityName);
        const named: DatabaseEntity = entityNameds.find((entity) => entity.name.toLowerCase() === fullName.toLowerCase()
            && entity.resourceType && item.EntityType && entity.resourceType.includes(item.EntityType.toLowerCase()));

        if (named) {
            named.mDEntity = {
                objectId: item.ObjectId,
                objectVersion: item.ObjectVersion,
                name: item.EntityName
            };
            // reset the alter action info in case the false using
            named.alterAction = undefined;
        }
    }

    private _generateDatabasePayloadForTelemetry(database: Database): { [key: string]: any } {
        const databaseTables = database.entities && database.entities.filter((e) => e.resourceType === 'tables');
        const tablesTelemetryObject: any[] = [];
        if (databaseTables && databaseTables.length > 0) {
            databaseTables.forEach((e: Table) => {
                const baseEntityReference = e.content &&
                    e.content.properties &&
                    e.content.properties.derivedModelEntityInfo &&
                    e.content.properties.derivedModelEntityInfo.entityDirectives &&
                    e.content.properties.derivedModelEntityInfo.entityDirectives.baseEntityReference ?
                    e.content.properties.derivedModelEntityInfo.entityDirectives.baseEntityReference :
                    '';

                const name = e.content ? e.content.name : e.name;
                tablesTelemetryObject.push({
                    inheritedFrom: baseEntityReference ? baseEntityReference.name : '',
                    isNameCustomized: baseEntityReference ? (baseEntityReference.name === name ? 'no' : 'yes') : ''
                });
            });
        }
        const databaseTelemetryObject = {
            name: database.content.name,
            baseModel: database.baseModel ? database.baseModel : '',
            tablesCount: databaseTables ? databaseTables.length : 0
        };

        return { database: databaseTelemetryObject, tables: tablesTelemetryObject };
    }
}
